import React, { useState, useEffect } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Alert from '../../components/ui/Alert';
import { locationActionService } from '../../services';
import type { LocationAction, CreateLocationActionData, LocationActionType } from '../../types';

interface LocationActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  locationId: number;
  action?: LocationAction | null; // null for create, action for edit
}

const ACTION_TYPE_OPTIONS = [
  { value: '', label: 'Select Action Type' },
  { value: 'GATE_CONTROLLER', label: '🚧 Gate Controller' },
  { value: 'SIREN', label: '🔊 Siren' },
  { value: 'CALL_911', label: '🚨 Call 911' },
  { value: 'CALL_SECURITY', label: '📞 Call Security' },
  { value: 'CALL_RESIDENT', label: '🏠 Call Resident' },
  { value: 'INTERCOM', label: '🔔 Intercom' },
];

// Types that require device configuration
const DEVICE_TYPES: LocationActionType[] = ['GATE_CONTROLLER', 'SIREN', 'INTERCOM'];
// Types that require phone configuration
const CALL_TYPES: LocationActionType[] = ['CALL_911', 'CALL_SECURITY', 'CALL_RESIDENT'];

const LocationActionModal: React.FC<LocationActionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  locationId,
  action,
}) => {
  const isEditMode = !!action;
  
  const [formData, setFormData] = useState<CreateLocationActionData>({
    action_type: 'GATE_CONTROLLER',
    name: '',
    description: '',
    api_url: '',
    device_port: 80,
    username: '',
    password: '',
    open_code: '',
    relay_number: 1,
    siren_type: 'snom',
    siren_command: 'Ringer5',
    siren_duration: 10,
    siren_sound_url: '',
    phone_number: '',
    caller_id: '',
    is_active: true,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or action changes
  useEffect(() => {
    if (isOpen) {
      if (action) {
        // Edit mode - populate form
        setFormData({
          action_type: action.action_type,
          name: action.name,
          description: action.description || '',
          api_url: action.api_url || '',
          device_port: action.device_port || 80,
          username: action.username || '',
          password: '', // Don't populate password for security
          open_code: action.open_code || '',
          relay_number: action.relay_number || 1,
          siren_type: action.siren_type || 'snom',
          siren_command: action.siren_command || 'Ringer5',
          siren_duration: action.siren_duration || 10,
          siren_sound_url: action.siren_sound_url || '',
          phone_number: action.phone_number || '',
          caller_id: action.caller_id || '',
          is_active: action.is_active,
        });
      } else {
        // Create mode - reset form
        setFormData({
          action_type: 'GATE_CONTROLLER',
          name: '',
          description: '',
          api_url: '',
          device_port: 80,
          username: '',
          password: '',
          open_code: '',
          relay_number: 1,
          siren_type: 'snom',
          siren_command: 'Ringer5',
          siren_duration: 10,
          siren_sound_url: '',
          phone_number: '',
          caller_id: '',
          is_active: true,
        });
      }
      setError(null);
    }
  }, [isOpen, action]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Build payload based on action type
      const payload: CreateLocationActionData = {
        action_type: formData.action_type,
        name: formData.name,
        description: formData.description || undefined,
        is_active: formData.is_active,
      };

      // Add device fields for device types
      if (DEVICE_TYPES.includes(formData.action_type)) {
        payload.api_url = formData.api_url;
        payload.device_port = formData.device_port || 80;
        if (formData.username) payload.username = formData.username;
        if (formData.password) payload.password = formData.password;
        
        // Gate-specific fields
        if (formData.action_type === 'GATE_CONTROLLER') {
          payload.open_code = formData.open_code;
          payload.relay_number = formData.relay_number || 1;
        }
        
        // Siren-specific fields
        if (formData.action_type === 'SIREN') {
          payload.siren_type = formData.siren_type;
          payload.siren_command = formData.siren_command;
          payload.siren_duration = formData.siren_duration || 10;
          if (formData.siren_sound_url) payload.siren_sound_url = formData.siren_sound_url;
        }
      }

      // Add phone fields for call types
      if (CALL_TYPES.includes(formData.action_type)) {
        payload.phone_number = formData.phone_number;
        payload.caller_id = formData.caller_id;
      }

      if (isEditMode && action) {
        await locationActionService.updateAction(action.id, payload);
      } else {
        await locationActionService.createAction(locationId, payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save action');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDeviceType = DEVICE_TYPES.includes(formData.action_type);
  const isCallType = CALL_TYPES.includes(formData.action_type);
  const isGateType = formData.action_type === 'GATE_CONTROLLER';
  const isSirenType = formData.action_type === 'SIREN';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Action' : 'Create Action'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Action Type */}
        <Select
          label="Action Type"
          name="action_type"
          value={formData.action_type}
          onChange={handleChange}
          options={ACTION_TYPE_OPTIONS}
          fullWidth
          disabled={isEditMode} // Can't change type in edit mode
        />

        {/* Name - Always shown */}
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Main Entrance Gate"
          required
          fullWidth
        />

        {/* Description - Always shown */}
        <Textarea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Optional description..."
          rows={2}
          fullWidth
        />

        {/* Device Configuration - For GATE, SIREN, INTERCOM */}
        {isDeviceType && (
          <>
            <div className="border-t border-surface-secondary pt-4">
              <h4 className="text-sm font-medium text-text-secondary mb-3">Device Configuration</h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="API URL / Device IP"
                  name="api_url"
                  value={formData.api_url}
                  onChange={handleChange}
                  placeholder="http://192.168.1.100/cgi-bin/ConfigManApp.com"
                  required
                  fullWidth
                />
                <Input
                  label="Device Port"
                  name="device_port"
                  type="number"
                  value={formData.device_port}
                  onChange={handleChange}
                  placeholder="80"
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Input
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="admin"
                  fullWidth
                />
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isEditMode ? '••••••••' : 'Enter password'}
                  helperText={isEditMode ? 'Leave blank to keep current password' : undefined}
                  fullWidth
                />
              </div>
            </div>

            {/* Gate-specific fields */}
            {isGateType && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Open Code (Fanvil)"
                  name="open_code"
                  value={formData.open_code}
                  onChange={handleChange}
                  placeholder="1234"
                  required
                  fullWidth
                />
                <Input
                  label="Relay Number"
                  name="relay_number"
                  type="number"
                  value={formData.relay_number}
                  onChange={handleChange}
                  placeholder="1"
                  fullWidth
                />
              </div>
            )}
            
            {/* Siren-specific fields */}
            {isSirenType && (
              <div className="border-t border-surface-secondary pt-4 mt-4">
                <h4 className="text-sm font-medium text-text-secondary mb-3">Siren Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Siren Device Type"
                    name="siren_type"
                    value={formData.siren_type || 'snom'}
                    onChange={handleChange}
                    options={[
                      { value: 'snom', label: 'Snom Phone' },
                      { value: 'generic_http', label: 'Generic HTTP Device' },
                      { value: 'custom', label: 'Custom Device' },
                    ]}
                    required
                    fullWidth
                  />
                  <Input
                    label="Duration (seconds)"
                    name="siren_duration"
                    type="number"
                    value={formData.siren_duration}
                    onChange={handleChange}
                    placeholder="10"
                    helperText="How long to play the siren"
                    fullWidth
                  />
                </div>
                
                {/* Snom-specific ringtone selection */}
                {formData.siren_type === 'snom' && (
                  <div className="mt-4">
                    <Select
                      label="Ringtone"
                      name="siren_command"
                      value={formData.siren_command || 'Ringer5'}
                      onChange={handleChange}
                      options={[
                        { value: 'Ringer1', label: 'Ringer 1' },
                        { value: 'Ringer2', label: 'Ringer 2' },
                        { value: 'Ringer3', label: 'Ringer 3' },
                        { value: 'Ringer4', label: 'Ringer 4' },
                        { value: 'Ringer5', label: 'Ringer 5 (Default)' },
                        { value: 'Ringer6', label: 'Ringer 6' },
                        { value: 'Ringer7', label: 'Ringer 7' },
                        { value: 'Ringer8', label: 'Ringer 8' },
                        { value: 'Ringer9', label: 'Ringer 9' },
                        { value: 'Ringer10', label: 'Ringer 10' },
                        { value: 'Silent', label: 'Silent' },
                        { value: 'Custom', label: 'Custom Sound' },
                      ]}
                      required
                      fullWidth
                    />
                    
                    {/* Show custom sound URL field if Custom selected */}
                    {formData.siren_command === 'Custom' && (
                      <Input
                        label="Custom Sound URL"
                        name="siren_sound_url"
                        value={formData.siren_sound_url}
                        onChange={handleChange}
                        placeholder="http://192.168.1.100/sounds/alarm.wav"
                        helperText="URL to WAV or MP3 audio file"
                        className="mt-4"
                        fullWidth
                      />
                    )}
                  </div>
                )}
                
                {/* Generic/Custom siren command */}
                {formData.siren_type !== 'snom' && (
                  <Input
                    label="Trigger Command"
                    name="siren_command"
                    value={formData.siren_command}
                    onChange={handleChange}
                    placeholder="e.g., /trigger or alarm_on"
                    helperText="Command or path to trigger the siren"
                    className="mt-4"
                    fullWidth
                  />
                )}
              </div>
            )}
          </>
        )}

        {/* Phone Configuration - For CALL_911, CALL_SECURITY */}
        {isCallType && (
          <div className="border-t border-surface-secondary pt-4">
            <h4 className="text-sm font-medium text-text-secondary mb-3">Phone Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="911 or +14165551234"
                required
                fullWidth
              />
              <Input
                label="Caller ID"
                name="caller_id"
                value={formData.caller_id}
                onChange={handleChange}
                placeholder="+14165551234"
                required
                fullWidth
              />
            </div>
          </div>
        )}

        {/* Active Status */}
        <div className="border-t border-surface-secondary pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleCheckboxChange}
              className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-sm text-text-secondary">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-surface-secondary">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update Action' : 'Create Action'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default LocationActionModal;
